import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, Zap, CheckCircle2 } from 'lucide-react';
import { buildSteps, type TutorialStep } from '@/data/onboardingSteps.tsx';
import { OnboardingPreview } from '@/components/desktop/OnboardingPreview';

interface OnboardingProps {
  isOpen: boolean;
  onFinish: () => void;
  sensorPrimerSeen: boolean;
  onSensorPrimerDone?: () => void;
  /** 快速回顾模式：精简卡片、只显步骤列表 */
  quickReview?: boolean;
  /** 快速回顾中点某步→切换到完整教程从该步开始 */
  onQuickReviewSelect?: (stepIndex: number) => void;
  /** 初始步骤（从快速回顾跳转时使用） */
  startStep?: number;
  t: any;
}

export function DesktopOnboarding({ isOpen, onFinish, sensorPrimerSeen, onSensorPrimerDone, quickReview, onQuickReviewSelect, startStep = 0, t }: OnboardingProps) {
  const [currentStep, setCurrentStep] = useState(startStep);
  const isZh = t?.langCode !== 'en';
  const ui = useCallback((zh: string, en: string) => isZh ? zh : en, [isZh]);

  /* ── 步骤数据（工厂 + useMemo 避免每次重建） ── */
  const steps = useMemo(() => buildSteps({ ui }), [ui]);

  /* ── 可⻅步奏：已看过传感器则跳过 step 0 ── */
  const visibleSteps = useMemo(() => sensorPrimerSeen ? steps.slice(1) : steps, [steps, sensorPrimerSeen]);

  const activeStepGlobal = sensorPrimerSeen ? currentStep + 1 : currentStep;
  const step = steps[activeStepGlobal];
  /* ── 键盘导航 ── */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setCurrentStep(prev => {
        const next = prev + 1;
        if (next < visibleSteps.length) return next;
        handleSkip();
        return prev;
      });
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentStep > 0) setCurrentStep(prev => prev - 1);
    }
    if (e.key === 'Escape') { e.preventDefault(); handleSkip(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentStep, visibleSteps.length]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /* ── 跳过 ── */
  const handleSkip = useCallback(() => {
    if (onSensorPrimerDone) onSensorPrimerDone();
    onFinish();
  }, [onSensorPrimerDone, onFinish]);

  /* ── 快速回顾模式：精简卡片、只显步骤列表可点击 ── */
  if (quickReview) {
    return (
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-auto">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()} // 阻止冒泡到背景关闭层
              className="relative w-full max-w-md rounded-3xl border border-white/10 bg-celestial-deep/95 p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-[0.22em] text-white/35">{ui('快速回顾', 'Quick Review')}</span>
                <button onClick={handleSkip} className="text-white/30 hover:text-white/70 text-sm font-black">{t?.close || ui('关闭', 'Close')}</button>
              </div>
              <div className="mt-4 space-y-1">
                {visibleSteps.map((item, index) => (
                  <button key={item.title} onClick={() => onQuickReviewSelect?.(index)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.06] text-left transition-colors">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${item.accent}`} />
                    <span className="text-sm font-bold text-white/65">{item.title}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { delay: 0.05 } }}
            className="absolute inset-0 bg-black/88 backdrop-blur-2xl" />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 18, transition: { delay: 0.02 } }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="relative grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-celestial-deep/95 shadow-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] md:min-h-[580px] min-h-0 max-h-[90vh] md:overflow-y-visible md:grid-cols-[260px_1fr] lg:grid-cols-[300px_1fr]"
          >
            {/* ── 左侧步骤导航（垂直时间轴） ── */}
            <aside className="border-b border-white/10 bg-white/[0.03] p-6 md:border-b-0 md:border-r">
              <div className="flex items-center gap-2">
                <div className="text-xs font-black uppercase tracking-[0.28em] text-white/40 bg-white/8 rounded-full px-3 py-1">
                  {currentStep + 1}/{visibleSteps.length}
                </div>
                <span className="text-xs font-bold text-white/30">{ui('入门引导', 'Onboarding')}</span>
              </div>
              <h2 className="mt-3 text-2xl font-black text-white">{ui('小枫 桌面教程', '小枫 Desktop Tutorial')}</h2>
              <p className="mt-3 text-sm leading-relaxed text-white/50">
                {ui(`跟着${visibleSteps.length}步快速入门`, `Follow ${visibleSteps.length} steps to get started`)}
              </p>

              <div className="mt-7 space-y-1">
                {visibleSteps.map((item, index) => {
                  const isActive = index === currentStep;
                  const isCompleted = index < currentStep;
                  return (
                    <button key={item.title} onClick={() => setCurrentStep(index)}
                      className={`flex w-full items-start gap-3 px-3 py-3 text-left transition-all rounded-2xl ${
                        isActive ? 'border border-white/18 bg-white/10 text-white' : 'border border-transparent text-white/45 hover:bg-white/[0.04] hover:text-white/70'
                      }`}>
                      <div className="flex flex-col items-center">
                        <span className={`h-3 w-3 rounded-full shrink-0 mt-1 ${isActive ? item.accent : isCompleted ? item.accent + ' opacity-60' : 'bg-white/14'}`}
                          style={isActive ? { boxShadow: '0 0 12px currentColor' } as React.CSSProperties : undefined} />
                        {index < visibleSteps.length - 1 && (
                          <div className={`w-px h-6 mt-1 ${isCompleted || isActive ? item.accent + ' opacity-30' : 'bg-white/10'}`} />
                        )}
                      </div>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[10px] font-black uppercase tracking-[0.18em] opacity-50">{item.eyebrow}</span>
                        <span className="mt-1 block text-sm font-bold truncate">{item.title}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* ── 右侧内容区 ── */}
            <section className="p-6 pt-4 md:p-8 md:pt-8 relative">
              <div className={`grid gap-7 ${step.preview ? 'lg:grid-cols-[1fr_360px]' : ''}`}>
                <div className="flex min-h-[380px] md:min-h-[420px] flex-col">
                  <motion.div
                    key={`copy-${currentStep}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex-1"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05]">
                      {step.icon}
                    </div>
                    <div className="mt-7 text-xs font-black uppercase tracking-[0.28em] text-white/35">{step.eyebrow}</div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-normal text-white">{step.title}</h1>
                    <p className="mt-5 max-w-2xl text-sm md:text-base leading-7 md:leading-8 text-white/62">{step.description}</p>

                    {/* Action 卡片：传感器步 = 权限说明，其他 = 操作指引 */}
                    <div className="mt-7 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs font-black uppercase tracking-[0.22em] text-white/35">
                        {step.isSensorStep ? ui('权限说明', 'Permissions') : ui('操作指引', 'How It Works')}
                      </div>
                      <div className="mt-2 text-base md:text-lg font-black text-white">{step.action}</div>
                    </div>

                    <div className="mt-5 grid gap-2">
                      {step.bullets.map(item => (
                        <div key={item} className="flex items-center gap-3 text-xs md:text-sm font-semibold text-white/58">
                          <CheckCircle2 size={16} className={`shrink-0 ${step.accentText}`} />{item}
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* ── 底部导航 ── */}
                  <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <button onClick={handleSkip}
                        className="h-11 shrink-0 whitespace-nowrap rounded-2xl border border-white/8 px-4 text-xs font-black uppercase tracking-[0.15em] text-white/30 transition-colors hover:text-white/60 hover:border-white/15">
                        {t.skip || ui('跳过教程', 'Skip')}
                      </button>
                      {currentStep > 0 && (
                        <button onClick={() => setCurrentStep(prev => prev - 1)}
                          className="flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-2xl border border-white/10 px-4 text-sm font-black text-white/55 transition-colors hover:bg-white/8 hover:text-white">
                          <ChevronLeft size={16} className="shrink-0" />{ui('上一步', 'Prev')}
                        </button>
                      )}
                    </div>

                    {currentStep < visibleSteps.length - 1 ? (
                      <button onClick={() => setCurrentStep(prev => prev + 1)}
                        className="flex h-12 min-w-32 items-center justify-center gap-2 rounded-2xl bg-white px-6 text-sm font-black text-black transition-transform hover:scale-[1.02] active:scale-95">
                        {ui('下一步', 'Next')}<ChevronRight size={18} />
                      </button>
                    ) : (
                      <button onClick={handleSkip}
                        className={`flex h-12 min-w-36 items-center justify-center gap-2 rounded-2xl px-6 text-sm font-black text-black shadow-[0_0_34px_rgba(255,200,80,0.25)] transition-all hover:scale-[1.02] active:scale-95 ${step.accent} motion-safe:animate-pulse`}>
                        {ui('进入桌面', 'Enter Desktop')}<Zap size={18} fill="currentColor" />
                      </button>
                    )}
                  </div>
                </div>

                    {/* ── 预览区 ── */}
                {step?.preview && (
                  <motion.div
                    key={`preview-${currentStep}`}
                    initial={{ opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 }}
                    className="md:pt-2 md:block hidden lg:block"
                  >
                    <OnboardingPreview previewType={step!.preview!} isZh={isZh} />
                  </motion.div>
                )}
              </div>
            </section>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
